import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Shield, Users, Zap, TrendingUp, CheckCircle, Sparkles, Smartphone, QrCode, Wrench, BarChart3, ArrowRight, Briefcase, Calculator, ShoppingCart, Package, Receipt, Wallet, FileText, LineChart, HandCoins } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AuthContext } from '../App';
import Footer from '../components/Footer';
import RoleDashboardPreview from '../components/RoleDashboardPreview';
import BrandLogo from '../components/BrandLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PlanComparison from '../components/PlanComparison';

const REDIRECT_URL = `${window.location.origin}/dashboard`;
const AUTH_URL = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(REDIRECT_URL)}`;

// UAE-based hero imagery (Dubai skyline / Abu Dhabi)
const UAE_HERO = 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1600&q=80';
const UAE_SKYLINE = 'https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1200&q=80';
const UAE_BUILDING = 'https://images.unsplash.com/photo-1572252009286-268acec5ca0a?auto=format&fit=crop&w=800&q=80';

export default function LandingPage() {
  const { loginDemo } = useContext(AuthContext);
  const handleLogin = () => { window.location.href = AUTH_URL; };
  const handleDemo = () => { loginDemo && loginDemo(); };

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white overflow-x-hidden">
      {/* Grain overlay */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-0"
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")'
        }}
      />

      {/* Header — sticky, always visible top nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0a0f1e]/85 border-b border-teal-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex justify-between items-center gap-3">
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Master brand asset — FM FLEXI PRO wordmark is baked into the icon */}
            <BrandLogo variant="icon" size={56} className="rounded-xl shadow-lg" />
            <div className="leading-tight hidden md:block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">by SKG Apps<span className="text-teal-400 ml-1">™</span></div>
            </div>
          </div>
          {/* Nav links — visible on ALL viewports */}
          <nav className="flex items-center gap-1 sm:gap-4 overflow-x-auto no-scrollbar">
            <a href="#intro" data-testid="nav-intro" className="text-xs sm:text-sm text-slate-300 hover:text-teal-400 transition whitespace-nowrap px-2">About</a>
            <a href="#profit-center" data-testid="nav-profit" className="text-xs sm:text-sm text-amber-300 hover:text-amber-200 transition whitespace-nowrap px-2 font-semibold">Profit Center</a>
            <a href="#role-views" data-testid="nav-roles" className="text-xs sm:text-sm text-slate-300 hover:text-teal-400 transition whitespace-nowrap px-2">Roles</a>
            <a href="#features" data-testid="nav-features" className="text-xs sm:text-sm text-slate-300 hover:text-teal-400 transition whitespace-nowrap px-2">Features</a>
            <a href="#pricing" data-testid="nav-modules" className="text-xs sm:text-sm text-slate-300 hover:text-teal-400 transition whitespace-nowrap px-2">Modules</a>
            <LanguageSwitcher compact dark />
            <Link
              to="/login"
              data-testid="header-login-btn"
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-4 sm:px-5 py-1.5 sm:py-2 rounded-full font-semibold text-xs sm:text-sm shadow-lg shadow-teal-500/30 whitespace-nowrap"
            >
              Sign In
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-16 pb-20 lg:pt-28 lg:pb-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/20 backdrop-blur-sm">
              <Sparkles className="w-3.5 h-3.5 text-teal-400" />
              <span className="text-xs font-medium tracking-wide text-teal-300">AI-ENABLED · BUILT FOR UAE</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Facilities Management,
              <br />
              <span className="bg-gradient-to-r from-teal-300 via-cyan-300 to-sky-300 bg-clip-text text-transparent">
                Beautifully Simple.
              </span>
            </h1>

            <p className="text-lg text-slate-300 leading-relaxed max-w-xl">
              <strong className="text-white">FM Flexi Pro™</strong> is the mobile-first, AI-enabled platform UAE property managers trust to run maintenance, cleaning, complaints, PPM, tenants and compliance — all from one elegant dashboard.
            </p>

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 pt-2 relative z-20">
              <Link
                data-testid="hero-get-started-btn"
                to="/login"
                className="group bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-7 py-3.5 rounded-full text-base font-semibold shadow-2xl shadow-teal-500/40 transition-all duration-300 hover:scale-[1.02] flex items-center justify-center whitespace-nowrap"
              >
                Sign In with Email
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Button
                data-testid="hero-try-demo-btn"
                onClick={handleDemo}
                className="px-7 py-3.5 rounded-full text-base font-semibold bg-amber-500 hover:bg-amber-600 text-slate-900 shadow-lg shadow-amber-500/30 whitespace-nowrap"
              >
                Try Demo (read-only)
              </Button>
              <Link
                data-testid="hero-live-walkthrough-btn"
                to="/demo-tour"
                className="px-7 py-3.5 rounded-full text-base font-semibold bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg shadow-purple-500/30 whitespace-nowrap flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" /> Live Walk-Through (multi-role)
              </Link>
              <Button
                data-testid="hero-google-btn"
                onClick={handleLogin}
                variant="outline"
                className="px-7 py-3.5 rounded-full text-base font-semibold bg-white/5 border-white/20 hover:bg-white/10 text-white whitespace-nowrap"
              >
                Continue with Google
              </Button>
            </div>

            <div className="flex items-center gap-6 pt-6 border-t border-slate-800/80">
              <div>
                <div className="text-2xl font-bold text-teal-400">50+</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Buildings Managed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-cyan-400">99.9%</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Uptime SLA</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-sky-400">24/7</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">UAE Support</div>
              </div>
            </div>
          </div>

          {/* Hero image - Dubai skyline */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-teal-500/20 via-cyan-500/20 to-transparent blur-3xl"></div>
            <div className="relative rounded-3xl overflow-hidden border border-teal-900/40 shadow-2xl shadow-teal-900/50">
              <img
                src={UAE_HERO}
                alt="Dubai skyline - smart facilities management in UAE"
                className="w-full h-[440px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1e] via-[#0a0f1e]/20 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 p-3 rounded-2xl backdrop-blur-xl bg-white/10 border border-white/10">
                <div className="p-2 rounded-xl bg-teal-500/20">
                  <Sparkles className="w-5 h-5 text-teal-300" />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-teal-300 uppercase tracking-wide font-semibold">AI Insight</div>
                  <div className="text-sm text-white">3 chillers at risk. Recommend service this week.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted / stats band */}
      <section className="relative z-10 border-y border-slate-800/60 bg-gradient-to-r from-slate-950 via-[#081424] to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          {[
            { label: 'Complaints', icon: CheckCircle },
            { label: 'PPM & Assets', icon: Wrench },
            { label: 'Tenants', icon: Users },
            { label: 'Compliance', icon: Shield }
          ].map((x) => (
            <div key={x.label} className="flex flex-col items-center gap-1.5">
              <x.icon className="w-5 h-5 text-teal-400" />
              <div className="text-slate-300 text-sm font-medium">{x.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Profit Center Section — positions the platform for Real Estate companies */}
      <section id="profit-center" className="relative z-10 py-20 lg:py-28 bg-gradient-to-b from-transparent via-amber-950/10 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
              <HandCoins className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-semibold tracking-[0.18em] text-amber-300 uppercase">Run FM as a Profit Center</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
              For Real Estate operators who want FM to <span className="bg-gradient-to-r from-amber-300 to-yellow-200 bg-clip-text text-transparent">earn, not just spend.</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-3xl mx-auto">
              Most FM software treats your operation as a cost center. <strong className="text-white">FM Flexi Pro™</strong> ships with built-in
              <strong className="text-amber-200"> HR, Payroll, Accounting, Sales Quotations, Inventory and Purchase</strong> modules — so every job ticket
              flows into a P&amp;L line. Bill tenants, track supplier margins, recover material costs and turn maintenance into measurable revenue.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="profit-center-grid">
            {[
              { icon: Briefcase, title: 'HR & Staff', desc: 'Manage technicians, attendance, leaves and role-based access — one source of truth for your workforce.', tint: 'amber' },
              { icon: Wallet, title: 'Payroll', desc: 'Generate payslips, track overtime, deductions and net pay — branded PDFs ready to download.', tint: 'yellow' },
              { icon: Calculator, title: 'Accounting', desc: 'Invoices, supplier bills, payments, AR/AP aging and a 22-line Chart of Accounts — purpose-built for FM.', tint: 'orange' },
              { icon: Receipt, title: 'Sales Quotations & Invoicing', desc: 'Quote → approve → convert to invoice with one click. Stamp-style approved PDFs ready for your client.', tint: 'amber' },
              { icon: Package, title: 'Inventory & Stock', desc: 'Live stock balance, material issue against complaints, audit-ready movement log.', tint: 'yellow' },
              { icon: ShoppingCart, title: 'Purchase & LPO', desc: 'Purchase Quotations, LPOs, Delivery Notes, Bill matching and Payment Vouchers — full procurement chain.', tint: 'orange' },
              { icon: LineChart, title: 'Sales & Purchase P&L', desc: 'Per-job margin rollup ties every LPO to its parent sales order. Know which contracts make money.', tint: 'amber' },
              { icon: FileText, title: 'Branded Documents', desc: 'Every PDF carries your logo, your address, your currency — invoices, payslips, LPOs, service reports.', tint: 'yellow' },
              { icon: TrendingUp, title: 'AI-Driven Insights', desc: 'Claude Sonnet AI flags risky assets and underperforming suppliers — protect margins before they erode.', tint: 'orange' }
            ].map((item, i) => (
              <div
                key={i}
                data-testid={`profit-card-${i}`}
                className="group relative p-6 rounded-2xl bg-gradient-to-br from-slate-900/90 to-slate-900/40 border border-amber-900/30 hover:border-amber-500/50 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-amber-900/30"
              >
                <div className={`inline-flex p-3 rounded-xl bg-${item.tint}-500/10 border border-${item.tint}-500/25 mb-4`}>
                  <item.icon className={`w-5 h-5 text-${item.tint}-300`} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent opacity-0 group-hover:opacity-100 transition" />
              </div>
            ))}
          </div>

          <div className="mt-12 max-w-4xl mx-auto p-6 sm:p-8 rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/5 via-yellow-500/5 to-transparent backdrop-blur-sm">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex flex-shrink-0 w-12 h-12 rounded-xl bg-amber-500/15 items-center justify-center">
                <Sparkles className="w-5 h-5 text-amber-300" />
              </div>
              <div className="flex-1">
                <h4 className="text-xl font-semibold text-white mb-2">From cost line to profit line — in one platform.</h4>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Property developers, owner-operators and FM contractors in the UAE use FM Flexi Pro to invoice tenants directly,
                  reconcile supplier statements per building, and report margin by contract or job — no separate accounting system needed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section id="intro" className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
        <div className="text-center">
          <div className="text-xs text-teal-400 font-semibold uppercase tracking-[0.2em] mb-3">Introduction</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            What is <span className="bg-gradient-to-r from-teal-300 to-cyan-300 bg-clip-text text-transparent">FM Flexi Pro?</span>
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mx-auto mb-6">
            <strong className="text-white">FM Flexi Pro™</strong> is an AI-enabled, mobile-first facilities management platform built specifically for UAE property managers.
            From a single dashboard you can run maintenance, cleaning routines, complaints, PPM schedules, statutory compliance, tenant communications, inventory, and finance —
            all under your company's branding, in your own currency.
          </p>
          <p className="text-base text-slate-400 leading-relaxed max-w-3xl mx-auto mb-8">
            Built by <strong className="text-teal-300">SKG Apps</strong>, FM Flexi Pro is "flexi" by design —
            start with FM today, then plug in Accounting, HR and other modules as your business grows. The platform is multi-tenant from day one
            (you manage your own clients and sub-tenants) and scales from a single tower to a portfolio of dozens.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto mt-8">
            <div className="p-5 rounded-xl bg-slate-900/40 border border-slate-800">
              <div className="text-3xl mb-1">🤖</div>
              <h4 className="font-semibold text-white mb-1">AI-Powered</h4>
              <p className="text-xs text-muted-foreground">Claude Sonnet predicts asset failures and writes executive reports.</p>
            </div>
            <div className="p-5 rounded-xl bg-slate-900/40 border border-slate-800">
              <div className="text-3xl mb-1">📱</div>
              <h4 className="font-semibold text-white mb-1">Mobile-First</h4>
              <p className="text-xs text-muted-foreground">Bottom-nav UI that technicians actually enjoy using on-site.</p>
            </div>
            <div className="p-5 rounded-xl bg-slate-900/40 border border-slate-800">
              <div className="text-3xl mb-1">🇦🇪</div>
              <h4 className="font-semibold text-white mb-1">Built in UAE</h4>
              <p className="text-xs text-muted-foreground">DEWA, Civil Defence, Dubai Municipality compliance baked in.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Role-based dashboard previews */}
      <RoleDashboardPreview />

      {/* Features */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
        <div className="max-w-2xl mb-14">
          <div className="text-xs text-teal-400 font-semibold uppercase tracking-[0.2em] mb-3">Modules</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Everything your FM team runs on,
            <span className="text-teal-300"> in one app.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: Building2, title: 'Multi-Building Portfolio', desc: 'Manage towers, villas, and commercial units across Dubai, Abu Dhabi and beyond.', color: 'teal' },
            { icon: Wrench, title: 'PPM + Asset Registry', desc: 'QR-coded assets, auto-scheduled preventive maintenance, photo evidence.', color: 'cyan' },
            { icon: Sparkles, title: 'Claude AI Insights', desc: 'Predictive maintenance, complaint triage, executive reports — powered by Claude Sonnet.', color: 'sky' },
            { icon: Shield, title: 'Statutory Compliance', desc: 'Civil Defence, DEWA, ADDC certificate renewals with proactive alerts.', color: 'teal' },
            { icon: Users, title: 'Tenants & Complaints', desc: 'Self-service tenant portal, SLA tracking, satisfaction ratings.', color: 'cyan' },
            { icon: QrCode, title: 'QR Code Scanning', desc: 'Scan any asset with your phone camera to view history and log maintenance.', color: 'sky' },
            { icon: Smartphone, title: 'Mobile-First', desc: 'Bottom-nav designed for technicians on-site. Works on any device.', color: 'teal' },
            { icon: BarChart3, title: 'Real-Time Dashboards', desc: 'CEO, Manager, Accountant and Technician views — with the KPIs that matter.', color: 'cyan' },
            { icon: TrendingUp, title: 'Extensible', desc: 'HR, Accounting and more coming soon — add modules as you grow.', color: 'sky' }
          ].map((f, i) => (
            <div
              key={i}
              className="group relative p-6 rounded-2xl bg-gradient-to-br from-slate-900/80 to-slate-900/30 backdrop-blur-sm border border-slate-800 hover:border-teal-500/50 transition-all duration-500 hover:-translate-y-1"
            >
              <div className={`inline-flex p-3 rounded-xl bg-${f.color}-500/10 border border-${f.color}-500/20 mb-4`}>
                <f.icon className={`w-5 h-5 text-${f.color}-400`} />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-white">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-500/50 to-transparent opacity-0 group-hover:opacity-100 transition"></div>
            </div>
          ))}
        </div>
      </section>

      {/* Plan Comparison */}
      <section id="plans" className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="text-xs text-teal-400 font-semibold uppercase tracking-[0.2em] mb-2">Subscription</div>
            <h2 className="text-3xl sm:text-5xl font-bold text-white">Choose the plan that fits your portfolio</h2>
            <p className="text-slate-400 mt-3 max-w-2xl mx-auto">Start with Basic FM and unlock Sales, Purchase and Accounting modules à la carte as your operations grow.</p>
          </div>
          <PlanComparison />
        </div>
      </section>

      {/* Why SKG + UAE visual */}
      <section id="pricing" className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <img
              src={UAE_SKYLINE}
              alt="UAE modern architecture"
              className="rounded-3xl w-full h-[480px] object-cover border border-slate-800"
              loading="lazy"
            />
          </div>
          <div className="space-y-6">
            <div className="text-xs text-teal-400 font-semibold uppercase tracking-[0.2em]">Why FM Flexi Pro</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
              Built in the UAE, for the way <span className="text-teal-300">UAE operates.</span>
            </h2>
            <ul className="space-y-4">
              {[
                { k: 'Multi-tenant at every level', v: 'SKG as Super-admin → Your company → Your sub-tenants. Clean data isolation by design.' },
                { k: 'Role-based menus', v: 'Full Access, Admin, Manager, CEO, Accountant, Tenant, Technician, CS Supervisor — each sees only what matters.' },
                { k: 'Mobile + Bottom Nav', v: 'Technicians work with their thumbs, not their keyboards. Tablet and phone ready.' },
                { k: 'Grows with you', v: 'Flexible platform — add Accounting, HR and custom modules as your business grows.' }
              ].map((p, i) => (
                <li key={i} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-teal-500/10 border border-teal-500/30 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-teal-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-white">{p.k}</div>
                    <div className="text-slate-400 text-sm">{p.v}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-20">
        <div className="relative rounded-3xl overflow-hidden border border-teal-500/30 p-10 lg:p-16 text-center">
          <div className="absolute inset-0 opacity-20">
            <img src={UAE_BUILDING} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-br from-teal-900/80 via-[#0a0f1e]/90 to-cyan-900/80"></div>
          <div className="relative space-y-6">
            <Sparkles className="w-10 h-10 mx-auto text-teal-300" />
            <h2 className="text-3xl sm:text-5xl font-bold text-white">Ready to run facilities smarter?</h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">Join UAE property teams using FM Flexi Pro™ to save hours every week.</p>
            <Button
              data-testid="cta-start-now-btn"
              onClick={handleLogin}
              className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 rounded-full text-base font-semibold shadow-2xl mt-4"
            >
              Start Free — Sign In with Google
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
